import React, {useEffect, useMemo} from 'react'
import {useForm} from 'react-hook-form'
import styled from 'styled-components'
import {
  AUTH_USERNAME_MAX_LENGTH,
  AUTHENTICATOR_OTP_LENGTH,
  REGEX_ALPHANUMERIC,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {SettingAuthenticatorRecoveryInput} from 'types'
import {Button, Input, Paragraph} from 'common/components'
import {VALIDATION_GIFTSHOP_AUTHENTICATOR_RECOVERY} from 'common/validations'
import convertUnit from 'lib/unit'
import {GiftShopSettingAuthenticatorSetupVerifProps} from './GiftShopSettingAuthenticatorSetup'

const StyledContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-self: center;
  background-color: ${({theme}) => theme.white_3};
`
const StyledContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  border-radius: ${convertUnit(16)};
  max-width: ${convertUnit(560)};
  margin-top: ${convertUnit(20)};
  height: fit-content;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    min-width: 100%;
    height: 100%;
    border-radius: 0;
    margin-top: 0;
  }
  background-color: ${({theme}) => theme.white_1};
`
const StyledSubtitle = styled(Paragraph)`
  text-align: center;
`
const StyledBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 ${convertUnit(20)};
  overflow-y: scroll;
  flex-grow: 1;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    width: 0;
  }
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }
`
const StyledTitle = styled(Paragraph)`
  margin: ${convertUnit(16)} 0 ${convertUnit(12)} 0;
  text-align: center;
`
const StyledFooter = styled.div`
  margin-bottom: ${convertUnit(20)};
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: ${convertUnit(4)};
  padding: 0 ${convertUnit(20)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    border-radius: 0;
  }
`
const StyledRecoveryInputContainer = styled.div`
  width: 100%;
  margin: ${convertUnit(20)} 0;
`

export default function GiftShopSettingAuthenticatorSetupInput({
  stateScreen,
  stateSecurityAnswer,
}: GiftShopSettingAuthenticatorSetupVerifProps) {
  const {translate} = useTranslation()
  const setScreen = stateScreen[1]
  const form = useForm<SettingAuthenticatorRecoveryInput>({
    defaultValues: {answer: ''},
    reValidateMode: 'onChange',
  })
  const {watch, errors, setValue} = form
  const {answer} = watch()
  const [ans, setAns] = stateSecurityAnswer

  const handleRenderRecoveryQuestion = useMemo(
    () => (
      <StyledRecoveryInputContainer>
        <Input
          name="answer"
          form={form}
          formRules={VALIDATION_GIFTSHOP_AUTHENTICATOR_RECOVERY}
          autoComplete={false}
          allowedCharacters={REGEX_ALPHANUMERIC}
          maxLength={AUTH_USERNAME_MAX_LENGTH}
          rightIcon="delete"
          label={translate('giftShop:settingAuthAppRecoveryQuestion')}
          labelDescription={translate(
            'giftShop:settingAuthAppRecoveryQuestionHint',
          )}
          labelDescriptionWeight="medium"
          labelDescriptionColor="gray_5"
          labelDescriptionDirection="column"
          placeholder={translate('giftShop:settingAuthAppRecoveryPlaceholder')}
          contentContainerStyle={{width: 'fill-available'}}
        />
      </StyledRecoveryInputContainer>
    ),
    [form, translate],
  )

  useEffect(() => {
    if (ans) {
      setValue('answer', ans)
    }
  }, [ans, setValue])

  return (
    <StyledContainer>
      <StyledContentContainer>
        <StyledBodyContainer>
          <StyledTitle fontSize="xl" fontWeight="bold">
            {translate('giftShop:settingAuthAppRecoverySetupTitle')}
          </StyledTitle>
          <StyledSubtitle fontSize="m" color="gray_5">
            {translate('giftShop:settingAuthAppRecoverySetupDesc', {
              length: AUTHENTICATOR_OTP_LENGTH,
            })}
          </StyledSubtitle>
          {handleRenderRecoveryQuestion}
        </StyledBodyContainer>
        <StyledFooter>
          <Paragraph fontSize="m" color="gray_5">
            {translate('global:step')
              .concat(' ')
              .concat(
                translate('giftShop:xOfy', {
                  x: 1,
                  y: 2,
                }),
              )}
          </Paragraph>
          <Button
            label={translate('global:save')}
            disabled={answer === '' || !!errors.answer}
            onClick={() => {
              setAns(answer)
              setScreen('setup')
            }}
          />
        </StyledFooter>
      </StyledContentContainer>
    </StyledContainer>
  )
}
